import { type CoverManifest } from '@soomo/lib/types/WebtextManifest';
import { type ThemeName } from '@soomo/lib/styles/themes/index';

type User = {
	email: string;
	first_name: string;
	last_name: string;
};

type BlankUser = {
	email: null;
	first_name: null;
	last_name: null;
};

type TrialConfiguration = {
	enabled: boolean;
	duration: number; //seconds
	duration_description: string; // Digit-form duration, e.g. "3 weeks"
	duration_description_spelled_out: string; // Spelled-out duration, e.g. "three weeks"
	duration_adjective: string; // Spelled-out adjectival duration, e.g. "three-week"
	percent_based: boolean; // Whether the trial is gated by percent_of_chapters rather than an explicit chapter list
	content_description: string; // Chapter-based description, e.g. "first three (3) chapters" (chapter-based trials only)
};

type SubscriptionConfiguration = {
	trial: TrialConfiguration;
	trial_start: string | null;
	trial_end: string | null;
	finalized: boolean;
	has_institutional_pay: boolean;
	status: string;
	cost: number;
	print_cost: number;
	duration: number;
	duration_description: string;
	payment_methods: {
		passkey: boolean;
		creditcard: boolean;
	};
};

export interface PrintCoverConfig {
	external_stylesheets?: string[];
	school_name?: string;
	front: PrintCoverFrontConfig;
}

export interface PrintCoverFrontConfig {
	url: string;
	details_bottom_offset_points?: number;
	details_left_offset_points?: number;
	author_top_offset_points?: number;
	bordered_header_color?: string;
	text_color?: string;
	edition_font?: string;
	dual_product_edition_font?: string;
	author_font?: string;
	copyright_font?: string;
}

type BaseCourse = {
	id: number;
	number: string;
	name: string;
	instructor: {
		id: number;
		name: string;
	};
	webtext: {
		theme_name: ThemeName;
		cover_manifest: CoverManifest;
	};
	subscription: SubscriptionConfiguration;
	print: {
		cover_config: PrintCoverConfig;
		ordered: boolean;
		payment_methods: {
			passkey: boolean;
			creditcard: boolean;
		};
	};
	retaking: boolean;
};

export interface RetakenCourse extends BaseCourse {
	subscription: SubscriptionConfiguration & {
		amount_paid: number;
		amount_owed: number;
	};
	retaking: true;
}

export function isCourseBeingRetaken(course): course is RetakenCourse {
	return course.retaking === true;
}

export interface FirstTimeCourse extends BaseCourse {
	subscription: SubscriptionConfiguration & {
		amount_paid: null;
		amount_owed: null;
	};
	retaking: false;
}

export interface BlankCourse {
	subscription: Record<string, never>;
	retaking?: never;
}

export type Course = FirstTimeCourse | RetakenCourse | BlankCourse;

export type Textbook = {
	desktop_img_url: string;
	print_img_url: string;
};

export type BlankTextbook = Record<string, never>;

export type InstitutionalCourse = {
	number: string;
	name: string;
};

export type School = {
	id: number;
	name: string;
	lms_school: boolean;
	lms_instructions: string;
	never_show_course_list: boolean;
};

export interface AppConfigWithCoursePresent extends BaseAppConfig {
	course: RetakenCourse | FirstTimeCourse;
	school: School;
	institutional_course: InstitutionalCourse;
}

export interface AppConfigWithoutCourseOrSchoolPresent extends BaseAppConfig {
	course: BlankCourse;
}

export interface AppConfigWithoutCourseButWithSchoolPresent extends BaseAppConfig {
	course: BlankCourse;
	school: School;
}

export function isCoursePresentInConfig(config): config is AppConfigWithCoursePresent {
	return config.course.id != null;
}

export function isSchoolPresentInConfig(
	config
): config is AppConfigWithCoursePresent | AppConfigWithoutCourseButWithSchoolPresent {
	return config.school != null;
}

export function areCourseAndSchoolBlankInConfig(
	config
): config is AppConfigWithoutCourseOrSchoolPresent {
	return config.course.id == null && config.school == null;
}

export type AppConfig =
	| AppConfigWithCoursePresent
	| AppConfigWithoutCourseButWithSchoolPresent
	| AppConfigWithoutCourseOrSchoolPresent;

interface BaseAppConfig {
	tos_html: string;
	tos_pdf_url: string | null;
	finalized: boolean;
	path_type:
		| 'LMS'
		| 'front_door'
		| 'front_door_returning'
		| 'prefilled'
		| 'complete_purchase'
		| 'print_purchase';
	path_config: {
		// Overrides for content in the path
		webtext_info_points?: string[];
		success_points?: string[];
		success_message?: string;
		welcome_step_ownership_phrasing?: string;
	};
	user: User | BlankUser;
	user_agent: {
		unsupported: boolean;
		use_legacy_inputs: boolean;
	};
	persist: boolean;
}
